<template>
    <!-- On mobile this bar is actually 2 bars, but on desktop it is only one. -->
    <div id="today-view__filter-bars"
         data-component="TodayViewFilterbar">
        <div class="today-view-filter-bar today-view-filter-bar--upper">
            <div class="today-view-filter-bar__left-btns">
                <div class="desktop-only">
                    <div class="flex flex--gutter-gap flex--align-stretch">
                        <div class="btn-group-flex flex--align-stretch">
                            <SquareBtn :color="selectedLayout === TodayViewLayoutName.LIST ? 'info' : 'white'"
                                       :active="selectedLayout === TodayViewLayoutName.LIST"
                                       data-analytics-id="`Today / List View"
                                       data-testid="today-list-view-button"
                                       @click="todayViewLayout(TodayViewLayoutName.LIST)">
                                <i class="btn-icon fa-solid fa-list" />
                            </SquareBtn>

                            <SquareBtn :color="selectedLayout === TodayViewLayoutName.LINEAR ? 'info' : 'white'"
                                       :active="selectedLayout === TodayViewLayoutName.LINEAR"
                                       data-analytics-id="`Today / Linear View"
                                       data-testid="today-linear-view-button"
                                       @click="todayViewLayout(TodayViewLayoutName.LINEAR)">
                                <i class="btn-icon fa-solid fa-chart-gantt" />
                            </SquareBtn>
                        </div>
                    </div>
                </div>
                <div class="mobile-only">
                    <div class="mobile-only">
                        <SquareBtn :color="hasFiltersSelected ? 'info' : 'white'"
                                   :active="hasFiltersSelected"
                                   :badge="filterCount"
                                   data-testid="today-show-filters-button"
                                   @click="showFilters">
                            <i class="btn-icon fa-solid fa-sliders" />
                        </SquareBtn>
                    </div>
                </div>
            </div>

            <DateSelector :initial-date="initialDate"
                          :allow-past-dates="false"
                          @change="dateChanged" />

            <div class="today-view-filter-bar__right-btns text-right">
                <div class="desktop-only">
                    <SquareBtn :color="hasFiltersSelected ? 'info' : 'white'"
                               :active="hasFiltersSelected"
                               :badge="filterCount"
                               @click="showFilters">
                        <i class="btn-icon fa-solid fa-sliders" />
                    </SquareBtn>
                </div>
                <SquareBtn :color="currentCartHasLines ? 'info' : 'white'"
                           :active="currentCartHasLines"
                           :badge="currentCartHasLines ? '•' : null"
                           data-testid="today-cart-preview-button"
                           @click="showCartPreview">
                    <i class="btn-icon fa-solid fa-shopping-cart" />
                </SquareBtn>
            </div>
        </div>
    </div>
</template>

<script>
import DateSelector from './DateSelector';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { getSelectedFilterCount } from '@/epos/vue/stores/modules/todayStore';
import { mapState } from 'vuex';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { TodayViewLayoutName } from '@/epos/vue/pages/today/TodayViewLayoutName';

export default {
    name: 'TodayViewFilterBar',

    components: {
        SquareBtn,
        DateSelector,
    },

    props: {
        initialDate: {
            type: Date,
            required: true,
        },
        selectedLayout: {
            type: String,
            default: null,
        },
    },

    emits: [
        'date-change',
        'filters-change',
        'layout-change',
    ],

    data() {
        return {
            TodayViewLayoutName,
        };
    },

    computed: {
        ...mapState(['isOnline']),

        ...mapState('cart', ['currentCartHasLines']),

        filterCount() {
            return getSelectedFilterCount(this.$store.state.today.filters);
        },

        hasFiltersSelected() {
            return this.filterCount > 0;
        },
    },

    methods: {
        dateChanged(newDate) {
            this.$emit('date-change', newDate);
        },

        showCartPreview() {
            container.vueModalFactory.openAndIgnoreResult(MODAL_NAMES__EPOS.CART_PREVIEW);
        },

        showFilters() {
            container.vueModalFactory
                .open(MODAL_NAMES__EPOS.TODAY_VIEW_FILTERS)
                .then((filters) => {
                    this.$emit('filters-change', filters);
                })
                .catch(() => {

                });
        },

        todayViewLayout(layout) {
            this.$emit('layout-change', layout);
        },
    },
};
</script>

<style lang="less">
@import "../../../../../less/variables";

#today-view__filter-bars {
    box-shadow: 1px 1px 5px rgba(0, 0, 0, .1);
}

.today-view-filter-bar {
    background: @light-grey;
    height: 12px + 40px + 12px;
    padding: 12px;
    border-bottom: 1px @dt-light-grey solid;

    position: relative; // So the shadow appears above the other content.

    display: flex;
    justify-content: center;
    grid-gap: 10px;

    &__left-btns,
    &__right-btns {
        display: flex;
        grid-gap: 10px;
    }

    &__left-btns {
        text-align: left;
        justify-content: flex-start;
    }

    .date-selector {
        height: 40px;
    }

    &__right-btns {
        text-align: right;
        justify-content: flex-end;
    }

    .btn {
        height: 40px;
        padding-top: 0;
        padding-bottom: 0;
    }

    .item-branch-selector {
        padding: 0;
        grid-gap: 0;
    }
}

@media (max-width: @mobile-max) {
    .today-view-filter-bar {
        &--upper {
            // On mobile the top bar is while and the lower bar is grey (grey is the default).
            background: @white;
        }

        &__left-btns {
            flex-grow: 1;
            flex-shrink: 0;
        }

        .date-selector {
            flex-grow: 0;
            flex-shrink: 1;
            width: 220px;

            .date-selector__date-display {
                font-size: @font-size-medium;
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
            }

            .date-selector__day-btn {
                display: none;
            }
        }

        &__right-btns {
            flex-grow: 1;
            flex-shrink: 0;
        }

        .item-branch-selector {
            flex-grow: 1;

            .branch-selector {
                width: 100%;

                &__btn-label {
                    left: 11px;
                    right: auto;
                }
            }
        }
    }
}

@media (min-width: @screen-sm-min) and (max-width: @screen-sm-max) {
    .today-view-filter-bar {
        .branch-selector {
            .text {
                // Hide the branch name on medium size screens (iPads) because it doesn't fit.
                display: none;
            }

            &--with-btn-label {
                > .btn {
                    padding-top: 0 !important;
                }
            }

            &__btn-label {
                display: none;
            }
        }
    }
}

@media (min-width: @non-mobile-min) {
    .today-view-filter-bar {
        padding: 12px 15px;

        &__left-btns {
            flex-grow: 1;
            flex-shrink: 0;
            flex-basis: 1%;
        }

        .date-selector {
            flex-grow: 0;
            flex-shrink: 0;
            width: 450px;
        }

        &__right-btns {
            flex-grow: 1;
            flex-shrink: 0;
            flex-basis: 1%;
        }

        .item-branch-selector {
            max-width: 144px;
        }
    }
}
</style>
